import type { RouteLocationNormalized, RouteLocationRaw, NavigationGuardNext } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { pilotageLocation } from './pilotageRoutes'
import { useProjectsStore } from '@/stores/projects'
import { useTenantsStore } from '@/stores/tenants'
import { logger } from './logger'
import { isPermissionDenied } from './access-errors'

/**
 * Creates a reusable route guard that checks a feature flag and redirects if disabled.
 *
 * @param featureFlagGetter - Function that returns the feature flag value (e.g., () => featureFlagsStore.isRiskAnalysisEnabled)
 * @param redirect - Either the NAME of the route to redirect to (the original
 *   form, unchanged for every existing caller), or a RESOLVER that returns the
 *   full target. The resolver form exists for IR-116 (review N1): the fallbacks
 *   used to hard-code the legacy assessments route, which is no longer the home
 *   of a non-legacy project — and deciding that needs an awaited read, which a
 *   route-name string cannot express.
 * @param redirectParams - Function that builds redirect params from the route (e.g., (to) => ({ pid: to.params.pid })). Ignored for the resolver form, which returns its own params.
 * @param debugName - Optional debug name for logging (defaults to 'Feature flag guard')
 * @returns A route guard function
 */
export function createFeatureFlagGuard(
  featureFlagGetter: () => boolean,
  redirect:
    | string
    | ((to: RouteLocationNormalized) => RouteLocationRaw | Promise<RouteLocationRaw>),
  redirectParams: (to: RouteLocationNormalized) => Record<string, string> = () => ({}),
  debugName: string = 'Feature flag guard'
): (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => Promise<boolean | void> {
  return async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    logger.debug(`${debugName}`)
    const appStore = useAppStore()

    // Ensure public config is loaded
    if (!appStore.publicConfigLoaded) {
      await appStore.loadPublicConfig()
    }

    // Check feature flag
    if (!featureFlagGetter()) {
      const target =
        typeof redirect === 'string'
          ? { name: redirect, params: redirectParams(to) }
          : await redirect(to)
      return next(target)
    }

    return true
  }
}

/**
 * Redesign (MYAC-1522, block 3) — the full-page conversation is Pilotage's
 * conversation tab (the letter): the old chat page leads there, same session,
 * so a bookmark or an old link never opens a second conversation page.
 * Only the chat route itself: its saved-prompt children stay where they are.
 */
export function createChatPageMovedGuard(
  chatRoute: string,
  track: 'qms' | 'td',
): (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => Promise<boolean | void> {
  return async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
    if (to.name !== chatRoute) return true
    return next(pilotageLocation(track, String(to.params.pid ?? ''), 'chat'))
  }
}

/**
 * Resolves access to the project targeted by `to.params.pid` for router guards.
 *
 * - In the user's project list, same tenant → proceed (`true`).
 * - In the user's list, *different* tenant → redirect to /dashboard and stage a
 *   cross-tenant prompt (offer to switch), returns `false`.
 * - NOT in the user's list → probe the doc once (it may be forbidden, missing, or
 *   the list may simply be stale / mid-bootstrap):
 *     - readable          → proceed (`true`),
 *     - permission-denied → /no-access, in the app's frame (returns `false`),
 *     - transient/unknown → proceed (`true`), let the view handle it.
 *
 * Note: under the current `projects` Firestore rules (which read `resource.data`
 * in every branch), a *nonexistent* project fails closed to `permission-denied`
 * rather than a clean `exists() === false` — so a missing project also routes to
 * /no-access. That is the secure default (it doesn't leak whether a project id
 * exists) and matches the "show access-denied, not a misleading 404" decision for
 * MYAC-758. The `!exists()` → 404 branch is kept only for correctness should the
 * rules ever permit reading absent docs.
 */
export async function ensureProjectInCurrentTenant(
  to: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<boolean> {
  const pid = to.params.pid as string | undefined
  if (!pid) return true

  const projectsStore = useProjectsStore()
  const tenantsStore = useTenantsStore()

  const project = projectsStore.projects.find((p) => p.id === pid)
  // Not in the user's loaded project list. Rather than proceed blindly (the view
  // then breaks on a permission-denied read or an empty doc), probe the project
  // doc once. A readable project proceeds — the list may just be stale or
  // mid-bootstrap; an unreadable one is access-denied. See the note above on why
  // a *missing* project also surfaces as permission-denied (→ /no-access) today.
  if (!project) {
    try {
      if (!(await projectsStore.projectExists(pid))) {
        next({ name: '404' })
        return false
      }
      return true
    } catch (error) {
      if (isPermissionDenied(error)) {
        // Inside the app's frame (MYAC-1522): the home guard has passed, so the
        // header and the chat stay; the bare `denied` page is for the frame's own failures.
        next({ name: 'project-denied' })
        return false
      }
      // Transient / unknown error on the probe: don't block — let the view load
      // and handle it, so a network blip never masquerades as denied/not-found.
      logger.warn('ensureProjectInCurrentTenant: project access probe failed; proceeding', error)
      return true
    }
  }

  const projectTenantId = project.tenant?.id
  const currentTenantId = tenantsStore.currentTenantId
  if (!projectTenantId || !currentTenantId) return true

  if (projectTenantId !== currentTenantId) {
    logger.debug('ensureProjectInCurrentTenant: cross-tenant access blocked', {
      pid,
      projectTenantId,
      currentTenantId,
    })

    // Stage the prompt with everything CrossTenantPromptDialog needs to either
    // perform the switch (then resume to redirectTo) or just dismiss. We only
    // surface the prompt if we can resolve the target tenant in the user's
    // accessible tenants — otherwise switching wouldn't be a valid option.
    const targetTenant = tenantsStore.tenants.find((t) => t.id === projectTenantId)
    if (targetTenant) {
      tenantsStore.setCrossTenantPrompt({
        projectId: project.id,
        projectName: project.name || project.id,
        targetTenantId: projectTenantId,
        targetTenantName: targetTenant.name || '',
        redirectTo: to.fullPath,
      })
    }

    next({ name: 'dashboard' })
    return false
  }

  return true
}
